import { from } from 'rxjs';
import { concatMap, map, switchMap, tap } from 'rxjs/operators';
import { authState } from 'rxfire/auth';
import { signOut, sendPasswordResetEmail } from 'firebase/auth';
import { FirebaseProvider } from '../../providers';

function FirebaseProviderProxy() {
  return from(import('../../providers')).pipe(
    map(({ FirebaseProvider }) => FirebaseProvider)
  );
}

function getAuthInstance() {
  return FirebaseProviderProxy().pipe(
    switchMap((provider) => provider.getInstance().getAuth())
  );
}

function getEmailAuthProvider(languageCode: string) {
  return FirebaseProviderProxy().pipe(
    switchMap((provider) => from(provider.getInstance().getAuth())),
    tap((auth) => (auth.languageCode = languageCode))
  );
}

function authStateObservable() {
  return getAuthInstance().pipe(
    switchMap((auth) => authState(auth))
  );
}

function getFacebookAuthProvider(language: string) {
  return FirebaseProviderProxy().pipe(
    switchMap((provider) =>
      from(provider.getInstance().getAuth()).pipe(
        map((auth) => {
          auth.languageCode = language.toLocaleUpperCase();
          return { provider: provider.getAuthProvider('facebook'), auth };
        })
      )
    )
  );
}

function getGoogleAuthProvider(language: string) {
  return FirebaseProviderProxy().pipe(
    switchMap((provider) =>
      from(provider.getInstance().getAuth()).pipe(
        map((auth) => {
          auth.languageCode = language.toLocaleUpperCase();
          return { provider: provider.getAuthProvider('google'), auth };
        })
      )
    )
  );
}

function logOutObservable() {
  return FirebaseProviderProxy().pipe(
    concatMap((provider) => provider.getInstance().getAuth()),
    map((auth) => signOut(auth))
  );
}

async function resetEmailPassword(email: string) {
  const auth = await FirebaseProvider.getInstance().getAuth();
  return sendPasswordResetEmail(auth, email);
}

export {
  authStateObservable,
  getAuthInstance,
  logOutObservable,
  getEmailAuthProvider,
  getFacebookAuthProvider,
  getGoogleAuthProvider,
  resetEmailPassword,
};
